import { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Heading,
  HStack,
  Input,
  Button,
  Table,
  Badge,
  IconButton,
  Text,
  Spinner,
  Card,
  SimpleGrid,
  Dialog,
} from "@chakra-ui/react";
import {
  FiEdit2,
  FiSearch,
  FiRefreshCw,
  FiCheck,
  FiPlus,
} from "react-icons/fi";
import api from "../lib/api";
import type {
  MasterDrug,
  PaginatedMasterDrugResponse,
  MasterDrugCreate,
} from "../types";
import AsyncTypeahead from "../components/AsyncTypeahead";
import { toaster } from "../components/ui/toaster";

// In v3, some components might be different.
// Using basic structure.

export default function MasterDrugIndexPage() {
  const [data, setData] = useState<PaginatedMasterDrugResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterNormalized, setFilterNormalized] = useState<string>("all");
  const [filterGroup, setFilterGroup] = useState("");

  // Edit State
  const [editingItem, setEditingItem] = useState<MasterDrug | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create State
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newItem, setNewItem] = useState<MasterDrugCreate>({
    brand_name: "",
    generic_name: "",
    drug_group: "",
    disease_target: "",
    category: "",
    is_normalized: false,
  });
  const [creating, setCreating] = useState(false);

  const fetchData = async (overrideSearch?: string) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: 20,
      };
      // Use override if provided, otherwise fall back to debounced value
      const query =
        overrideSearch !== undefined ? overrideSearch : debouncedSearch;
      if (query) params.search = query;

      if (filterNormalized !== "all")
        params.is_normalized = filterNormalized === "true";
      if (filterGroup) params.drug_group = filterGroup;

      const res = await api.get("/admin/master-drugs", { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toaster.create({
        title: "Error fetching data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Debounce logic for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (search.length >= 3) {
        setPage(1);
        setDebouncedSearch(search);
      } else if (search.length === 0) {
        setPage(1);
        setDebouncedSearch("");
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [page, debouncedSearch, filterNormalized]);

  const handleSearch = () => {
    setPage(1);
    fetchData(search);
  };

  const handleEditClick = (item: MasterDrug) => {
    setEditingItem({ ...item });
    setIsEditOpen(true);
  };

  const handleCreateOpen = () => {
    setNewItem({
      brand_name: "",
      generic_name: "",
      drug_group: "",
      disease_target: "",
      category: "",
      is_normalized: false,
    });
    setIsCreateOpen(true);
  };

  const handleSave = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await api.patch(`/admin/master-drugs/${editingItem.id}`, {
        brand_name: editingItem.brand_name,
        generic_name: editingItem.generic_name,
        drug_group: editingItem.drug_group,
        disease_target: editingItem.disease_target,
        category: editingItem.category,
        is_normalized: editingItem.is_normalized,
      });

      toaster.create({
        title: "Updated successfully",
        type: "success",
      });
      setIsEditOpen(false);
      fetchData(); // Refresh to see changes (and new embeddings technically happen on backend)
    } catch (err) {
      console.error(err);
      toaster.create({
        title: "Update failed",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSave = async () => {
    setCreating(true);
    try {
      await api.post("/admin/master-drugs/", newItem);
      toaster.create({
        title: "Drug created successfully",
        type: "success",
      });
      setIsCreateOpen(false);
      fetchData();
    } catch (err) {
      console.error(err);
      toaster.create({
        title: "Creation failed",
        type: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box p={5}>
      <VStack align="stretch" spaceY={6}>
        <HStack justify="space-between" wrap={"wrap"}>
          <Heading size="xl">Master Drug Index</Heading>
          <HStack>
            <Button onClick={handleCreateOpen} colorPalette="blue" size="sm">
              <FiPlus /> Add Drug
            </Button>
            <Button onClick={() => fetchData()} variant="outline" size="sm">
              <FiRefreshCw />
            </Button>
          </HStack>
        </HStack>

        <Card.Root>
          <Card.Body>
            <SimpleGrid columns={{ base: 1, md: 4 }} gap={4}>
              <Input
                placeholder="Search brand, generic..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Input
                placeholder="Filter by Drug Group"
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              {/* 
                  Note: Select implementation varies in v3 (NativeSelect vs Select). 
                  Using native select for simplicity 
               */}
              <select
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
                value={filterNormalized}
                onChange={(e) => {
                  setFilterNormalized(e.target.value);
                  setPage(1);
                }}>
                <option value="all">All Normalization Status</option>
                <option value="true">Normalized</option>
                <option value="false">Not Normalized</option>
              </select>
              <Button onClick={handleSearch}>
                <FiSearch /> Search
              </Button>
            </SimpleGrid>
          </Card.Body>
        </Card.Root>

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
          </Box>
        ) : (
          <Box overflowX="auto" borderWidth="1px" borderRadius="lg">
            <Table.Root striped interactive>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>ID</Table.ColumnHeader>
                  <Table.ColumnHeader>Brand Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Generic Name</Table.ColumnHeader>
                  <Table.ColumnHeader>Group</Table.ColumnHeader>
                  <Table.ColumnHeader>Target</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Actions</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data?.items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>{item.id}</Table.Cell>
                    <Table.Cell fontWeight="medium">
                      {item.brand_name || "-"}
                    </Table.Cell>
                    <Table.Cell>{item.generic_name || "-"}</Table.Cell>
                    <Table.Cell>{item.drug_group || "-"}</Table.Cell>
                    <Table.Cell>{item.disease_target || "-"}</Table.Cell>
                    <Table.Cell>
                      {item.is_normalized ? (
                        <Badge colorPalette="green">
                          <FiCheck /> Normalized
                        </Badge>
                      ) : (
                        <Badge colorPalette="yellow">Pending</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <IconButton
                        aria-label="Edit"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditClick(item)}>
                        <FiEdit2 />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {!data || data.items.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7} textAlign="center">
                      No data found
                    </Table.Cell>
                  </Table.Row>
                ) : null}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {/* Pagination Controls */}
        {data && (
          <HStack justify="center" mt={4}>
            <Button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              variant="outline">
              Previous
            </Button>
            <Text>
              Page {page} of {data.pages}
            </Text>
            <Button
              disabled={page >= data.pages}
              onClick={() => setPage((p) => p + 1)}
              variant="outline">
              Next
            </Button>
          </HStack>
        )}
      </VStack>

      {/* Edit Modal - Using Dialog in V3 */}
      {isEditOpen && editingItem && (
        <Dialog.Root
          open={isEditOpen}
          onOpenChange={(e) => setIsEditOpen(e.open)}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Edit Drug #{editingItem.id}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack spaceY={4} align="stretch">
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      Brand Name
                    </Text>
                    <AsyncTypeahead
                      value={editingItem.brand_name || ""}
                      onChange={(val) =>
                        setEditingItem({ ...editingItem, brand_name: val })
                      }
                      field="brand_name"
                      placeholder="Search existing brands or type new..."
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      Generic Name
                    </Text>
                    <Input
                      value={editingItem.generic_name || ""}
                      onChange={(e) =>
                        setEditingItem({
                          ...editingItem,
                          generic_name: e.target.value,
                        })
                      }
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      Drug Group
                    </Text>
                    <AsyncTypeahead
                      value={editingItem.drug_group || ""}
                      onChange={(val) =>
                        setEditingItem({ ...editingItem, drug_group: val })
                      }
                      field="drug_group"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      Disease Target
                    </Text>
                    <AsyncTypeahead
                      value={editingItem.disease_target || ""}
                      onChange={(val) =>
                        setEditingItem({ ...editingItem, disease_target: val })
                      }
                      field="disease_target"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={1}>
                      Category
                    </Text>
                    <AsyncTypeahead
                      value={editingItem.category || ""}
                      onChange={(val) =>
                        setEditingItem({ ...editingItem, category: val })
                      }
                      field="category"
                    />
                  </Box>

                  <HStack>
                    <Text fontSize="sm">Is Normalized?</Text>
                    <Button
                      size="xs"
                      variant={editingItem.is_normalized ? "solid" : "outline"}
                      colorPalette={
                        editingItem.is_normalized ? "green" : "gray"
                      }
                      onClick={() =>
                        setEditingItem({
                          ...editingItem,
                          is_normalized: !editingItem.is_normalized,
                        })
                      }>
                      {editingItem.is_normalized ? "Yes" : "No"}
                    </Button>
                  </HStack>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditOpen(false)}>
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
                <Button loading={saving} onClick={handleSave}>
                  Save Changes
                </Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger />
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}

      {/* Create Modal */}
      <Dialog.Root
        open={isCreateOpen}
        onOpenChange={(e) => setIsCreateOpen(e.open)}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Add New Master Drug</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack spaceY={4} align="stretch">
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Brand Name
                  </Text>
                  <AsyncTypeahead
                    value={newItem.brand_name || ""}
                    onChange={(val) =>
                      setNewItem({ ...newItem, brand_name: val })
                    }
                    field="brand_name"
                    placeholder="Search or create brand..."
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Generic Name
                  </Text>
                  <Input
                    value={newItem.generic_name || ""}
                    onChange={(e) =>
                      setNewItem({ ...newItem, generic_name: e.target.value })
                    }
                    placeholder="Enter generic name"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Drug Group
                  </Text>
                  <AsyncTypeahead
                    value={newItem.drug_group || ""}
                    onChange={(val) =>
                      setNewItem({ ...newItem, drug_group: val })
                    }
                    field="drug_group"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Disease Target
                  </Text>
                  <AsyncTypeahead
                    value={newItem.disease_target || ""}
                    onChange={(val) =>
                      setNewItem({ ...newItem, disease_target: val })
                    }
                    field="disease_target"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    Category
                  </Text>
                  <AsyncTypeahead
                    value={newItem.category || ""}
                    onChange={(val) =>
                      setNewItem({ ...newItem, category: val })
                    }
                    field="category"
                  />
                </Box>

                <HStack>
                  <Text fontSize="sm">Is Normalized?</Text>
                  <Button
                    size="xs"
                    variant={newItem.is_normalized ? "solid" : "outline"}
                    colorPalette={newItem.is_normalized ? "green" : "gray"}
                    onClick={() =>
                      setNewItem({
                        ...newItem,
                        is_normalized: !newItem.is_normalized,
                      })
                    }>
                    {newItem.is_normalized ? "Yes" : "No"}
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
              </Dialog.ActionTrigger>
              <Button loading={creating} onClick={handleCreateSave}>
                Create Drug
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
}
